import axios from 'axios';

export interface MoviePoster {
  posterUrl?: string;
  source: 'tmdb' | 'omdb' | 'placeholder';
  tmdbId?: number;
}

class PosterService {
  private readonly API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  
  // Keep a small in-memory cache for the current session
  private sessionCache = new Map<string, MoviePoster>();

  private createCacheKey(movieId: number): string {
    return `movie_${movieId}`;
  }

  async getMoviePoster(movieId: number): Promise<MoviePoster> {
    const cacheKey = this.createCacheKey(movieId);
    
    // Check session cache first
    if (this.sessionCache.has(cacheKey)) {
      return this.sessionCache.get(cacheKey)!;
    }

    try {
      const response = await axios.get(`${this.API_BASE_URL}/movies/${movieId}/poster`, {
        timeout: 10000,
      });

      const posterData: MoviePoster = response.data.poster_data;
      
      // Cache in session
      this.sessionCache.set(cacheKey, posterData);
      
      return posterData;
    } catch (error) {
      console.warn(`Failed to fetch poster for movie ${movieId}:`, error);
      
      // Return placeholder on error
      const placeholder: MoviePoster = {
        posterUrl: `https://via.placeholder.com/300x450/374151/f9fafb?text=${encodeURIComponent('No Poster')}`,
        source: 'placeholder'
      };
      
      this.sessionCache.set(cacheKey, placeholder);
      return placeholder;
    }
  }

  // Batch poster fetching for multiple movies
  async getMoviePosters(movieIds: number[]): Promise<MoviePoster[]> {
    const posterPromises = movieIds.map(movieId => 
      this.getMoviePoster(movieId)
    );

    return Promise.all(posterPromises);
  }

  // Clear session cache
  clearCache(): void {
    this.sessionCache.clear();
  }

  // Get cache size for debugging
  getCacheSize(): number {
    return this.sessionCache.size;
  }
}

export const posterService = new PosterService();
export default posterService;
